import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { isType } from '@sorare/core/src/lib/gql';

import { RewardsFlow } from './RewardsFlow';
import type {
  LastStoryFixtureQuery,
  LastStoryFixtureQueryVariables,
  LazyLastFixtureStory_story,
} from './__generated__/index.graphql';
import { useDisplayFixtureStory } from './useDisplayFixtureStory';

const LAST_STORY_FIXTURE_QUERY = gql`
  query LastStoryFixtureQuery($sport: Sport!) {
    currentUser {
      slug
      ...RewardsFlow_currentUser
    }
    so5 {
      cardXpGradeThreshold(sport: $sport) {
        ...RewardsFlow_cardXpGradeThreshold
      }
      myAvailableFixtureStory(sport: $sport) {
        id
        aasmState
        completedThresholdsStreakTasks {
          id
          ...RewardsFlow_thresholdsStreakTask
        }
        leveledUpCards {
          id
          ...RewardsFlow_leveledUpCard
        }
        userGroupMembershipShopItemUpdates {
          id
          ...RewardsFlow_userGroupMembershipShopItemsUpdate
        }
        readable {
          ... on So5Fixture {
            slug
            mySo5Rewards {
              slug
              ...RewardsFlow_so5Reward
            }
          }
          ...RewardsFlow_so5Fixture
        }
      }
    }
  }
  ${RewardsFlow.fragments.so5Reward}
  ${RewardsFlow.fragments.so5Fixture}
  ${RewardsFlow.fragments.leveledUpCard}
  ${RewardsFlow.fragments.thresholdsStreakTask}
  ${RewardsFlow.fragments.taskInterface}
  ${RewardsFlow.fragments.currentUser}
  ${useDisplayFixtureStory.fragments.story}
  ${RewardsFlow.fragments.cardXpGradeThreshold}
  ${RewardsFlow.fragments.userGroupMembershipShopItemsUpdate}
` as TypedDocumentNode<LastStoryFixtureQuery, LastStoryFixtureQueryVariables>;

const LastFixtureStory = () => {
  const { sport } = useSportContext();
  const { data } = useQuery(LAST_STORY_FIXTURE_QUERY, {
    variables: {
      sport,
    },
  });
  const [storyOpened, setStoryOpened] = useState(true);

  const { currentUser, so5 } = data || {};
  const {
    id,
    readable: so5Fixture,
    leveledUpCards,
    completedThresholdsStreakTasks,
    userGroupMembershipShopItemUpdates,
  } = so5?.myAvailableFixtureStory || {};
  if (!id || !so5Fixture || !isType(so5Fixture, 'So5Fixture') || !storyOpened) {
    return null;
  }

  return (
    <RewardsFlow
      storyId={id}
      currentUser={currentUser}
      so5Fixture={so5Fixture}
      completedThresholdsStreakTasks={completedThresholdsStreakTasks}
      leveledUpCards={leveledUpCards}
      gradeThresholds={data?.so5.cardXpGradeThreshold}
      so5Rewards={so5Fixture.mySo5Rewards}
      userGroupMembershipShopItemUpdates={userGroupMembershipShopItemUpdates}
      onClose={() => setStoryOpened(false)}
    />
  );
};

type LazyLastFixtureStoryProps = {
  story: LazyLastFixtureStory_story;
};
export const LazyLastFixtureStory = ({ story }: LazyLastFixtureStoryProps) => {
  const displayFixtureStory = useDisplayFixtureStory(story);

  const { id, readable: so5Fixture } = story;

  if (
    displayFixtureStory &&
    id &&
    so5Fixture &&
    isType(so5Fixture, 'So5Fixture')
  ) {
    return <LastFixtureStory />;
  }
  return null;
};

LazyLastFixtureStory.fragments = {
  story: gql`
    fragment LazyLastFixtureStory_story on Story {
      id
      readable {
        ... on So5Fixture {
          slug
        }
      }
      ...useDisplayFixtureStory_story
    }
    ${useDisplayFixtureStory.fragments.story}
  ` as TypedDocumentNode<LazyLastFixtureStory_story>,
};
